import type { FC, ReactNode } from 'react'
import type { ContentBlock, Settings } from '@/types/content'

import Link from 'next/link'
import { ArrowUp, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { Container } from '@/components/generals/container'
import { CtaButton } from '@/components/generals/cta-button'

import {
    cms,
    hasValue,
    toExternalHref,
    toTelHref,
    toWhatsAppHref
} from '@/lib/cms'

import { legalNavigation, mainNavigation } from '@/constants/site'

interface SiteFooterProps {
    settings: Settings
    statement?: ContentBlock | null
}

interface FooterColumnProps {
    title: string
    children: ReactNode
}

const FooterColumn: FC<FooterColumnProps> = ({ title, children }): ReactNode => {
    return (
        <div className='flex flex-col gap-5'>
            <h2 className='type-eyebrow text-ash'>
                {title}
            </h2>

            {children}
        </div>
    )
}

const linkClass = 'link-underline self-start text-[0.9375rem] text-bone/85 transition-colors duration-300 hover:text-bone'

export const SiteFooter: FC<SiteFooterProps> = ({ settings, statement }): ReactNode => {
    const socials = [
        { label: 'Instagram', column: 'instagram', value: settings.instagram, platform: 'instagram' as const },
        { label: 'TikTok', column: 'tiktok', value: settings.tiktok, platform: 'tiktok' as const },
        { label: 'YouTube', column: 'youtube', value: settings.youtube, platform: 'youtube' as const }
    ].filter(social => hasValue(social.value))

    return (
        <footer id='site-footer' className='relative overflow-hidden bg-ink text-bone'>
            <div aria-hidden='true' className='grain-layer' />

            <Container className='relative pt-20 sm:pt-28'>
                <div className='grid gap-12 border-b border-ink-line pb-16 lg:grid-cols-12 lg:gap-8 lg:pb-20'>
                    <div className='flex flex-col gap-6 lg:col-span-7'>
                        {statement && hasValue(statement.ueberschrift) && (
                            <p
                                data-mdlfy-cms={cms('inhalte', statement.id, 'ueberschrift')}
                                className='type-display whitespace-pre-line text-display-md text-bone'
                            >
                                {statement.ueberschrift}
                            </p>
                        )}

                        {statement && hasValue(statement.text) && (
                            <p
                                data-mdlfy-cms={cms('inhalte', statement.id, 'text')}
                                className='max-w-md text-[0.9375rem] leading-relaxed text-ash'
                            >
                                {statement.text}
                            </p>
                        )}
                    </div>

                    <div className='flex items-start lg:col-span-5 lg:justify-end'>
                        <CtaButton settings={settings} tone='dark' size='lg' />
                    </div>
                </div>

                <div className='grid grid-cols-2 gap-x-6 gap-y-12 py-16 md:grid-cols-4 lg:py-20'>
                    <FooterColumn title='Navigation'>
                        <ul className='flex flex-col gap-3'>
                            {mainNavigation.map(item => (
                                <li key={item.href} className='flex'>
                                    <Link href={item.href} className={linkClass}>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </FooterColumn>

                    <FooterColumn title='Kontakt'>
                        <ul className='flex flex-col gap-3 text-[0.9375rem] text-bone/85'>
                            <li className='flex'>
                                {hasValue(settings.email)
                                    ? (
                                        <a href={`mailto:${settings.email}`} className={cn(linkClass, 'break-all')}>
                                            <span data-mdlfy-cms={cms('einstellungen', settings.id, 'email')}>{settings.email}</span>
                                        </a>
                                    )
                                    : <span data-mdlfy-cms={cms('einstellungen', settings.id, 'email')} data-empty-hint='E-Mail-Adresse ergänzen' className='is-placeholder' />}
                            </li>

                            <li className='flex'>
                                {hasValue(settings.telefon)
                                    ? (
                                        <a href={toTelHref(settings.telefon)} className={linkClass}>
                                            <span data-mdlfy-cms={cms('einstellungen', settings.id, 'telefon')}>{settings.telefon}</span>
                                        </a>
                                    )
                                    : <span data-mdlfy-cms={cms('einstellungen', settings.id, 'telefon')} data-empty-hint='Telefonnummer ergänzen' className='is-placeholder' />}
                            </li>

                            {hasValue(settings.whatsapp) && (
                                <li className='flex'>
                                    <a href={toWhatsAppHref(settings.whatsapp)} target='_blank' rel='noopener noreferrer' className={linkClass}>
                                        WhatsApp
                                    </a>
                                </li>
                            )}

                            <li className='flex text-ash'>
                                <span
                                    data-mdlfy-cms={cms('einstellungen', settings.id, 'standort')}
                                    data-empty-hint='Trainingsort oder Region ergänzen'
                                    className={cn(!hasValue(settings.standort) && 'is-placeholder')}
                                >
                                    {settings.standort}
                                </span>
                            </li>
                        </ul>
                    </FooterColumn>

                    <FooterColumn title='Social Media'>
                        {socials.length > 0
                            ? (
                                <ul className='flex flex-col gap-3'>
                                    {socials.map(social => (
                                        <li key={social.column} className='flex'>
                                            <a
                                                href={toExternalHref(social.value as string, social.platform)}
                                                target='_blank'
                                                rel='noopener noreferrer'
                                                className={cn(linkClass, 'group inline-flex items-center gap-1.5')}
                                            >
                                                {social.label}
                                                <ArrowUpRight aria-hidden='true' className='size-3.5 opacity-60 transition-transform duration-300 group-hover:rotate-45' />
                                            </a>
                                        </li>
                                    ))}
                                </ul>
                            )
                            : (
                                <p className='is-placeholder self-start text-sm text-ash'>
                                    Links zu Instagram, TikTok oder YouTube in den Einstellungen ergänzen
                                </p>
                            )}
                    </FooterColumn>

                    <FooterColumn title='Rechtliches'>
                        <ul className='flex flex-col gap-3'>
                            {legalNavigation.map(item => (
                                <li key={item.href} className='flex'>
                                    <Link href={item.href} className={linkClass}>
                                        {item.label}
                                    </Link>
                                </li>
                            ))}
                        </ul>
                    </FooterColumn>
                </div>
            </Container>

            <div aria-hidden='true' className='relative flex select-none justify-center overflow-hidden px-3'>
                <p
                    data-mdlfy-cms={cms('einstellungen', settings.id, 'markenname')}
                    className='type-display whitespace-nowrap text-[13.6vw] leading-[0.78] text-bone/[0.08] 2xl:text-[12.25rem]'
                >
                    {settings.markenname}
                </p>
            </div>

            <Container className='relative'>
                <div className='flex flex-col gap-4 border-t border-ink-line py-8 text-sm text-ash sm:flex-row sm:items-center sm:justify-between'>
                    <p>
                        <span>© </span>
                        <span suppressHydrationWarning>{new Date().getFullYear()}</span>
                        <span> </span>
                        <span data-mdlfy-cms={cms('einstellungen', settings.id, 'markenname')}>{settings.markenname}</span>
                        <span>. Alle Rechte vorbehalten.</span>
                    </p>

                    <a href='#top' className='group inline-flex items-center gap-2 self-start transition-colors duration-300 hover:text-bone sm:self-auto'>
                        Nach oben
                        <ArrowUp aria-hidden='true' className='size-4 transition-transform duration-300 group-hover:-translate-y-0.5' />
                    </a>
                </div>
            </Container>
        </footer>
    )
}
